import React, { useCallback, useEffect, useState } from 'react';
import { Card, Statistic, Button, Modal, Row, Col, Typography, message, Tag, Tooltip, Space, Alert, Checkbox } from 'antd';
import { CreditCardOutlined, ThunderboltOutlined, WarningOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import PixPaymentPanel from './PixPaymentPanel';
import CardPaymentPanel from './CardPaymentPanel';
import { isConfigured as mpConfigured } from '../../../utils/mpSdk';

const { Text, Title } = Typography;
const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

// Mostra saldo de credito + botao pra comprar pack.
// props: { communityTeamId, onChange? }
export default function CreditsPurchaseCard({ communityTeamId, onChange }) {
  const nav = useNavigate();
  const [balance, setBalance] = useState(0);
  const [packs, setPacks] = useState([]);
  const [modal, setModal] = useState(false);
  const [mpConnected, setMpConnected] = useState(null);

  // Fluxo inline: step 0 escolhe pack, step 1 escolhe metodo, step 2 paga.
  const [step, setStep] = useState(0);
  const [selectedPack, setSelectedPack] = useState(null);
  const [method, setMethod] = useState(null);
  const [pixPurchase, setPixPurchase] = useState(null);
  const [pixLoading, setPixLoading] = useState(false);
  const [cardSubmitting, setCardSubmitting] = useState(false);
  // "Registrar como saída na conta corrente do time" — opt-in.
  const [registerExpense, setRegisterExpense] = useState(false);

  const load = useCallback(async () => {
    if (!communityTeamId) return;
    try {
      const r = await api.get('/community/finance/credits/status', { params: { team_id: communityTeamId } });
      setBalance(r.data.balance);
      setPacks(r.data.packs || []);
    } catch (e) { /* silent */ }
  }, [communityTeamId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!communityTeamId) return;
    api.get('/community/finance/mp-connection/status', { params: { team_id: communityTeamId } })
      .then(r => setMpConnected(!!r.data.connected))
      .catch(() => setMpConnected(false));
  }, [communityTeamId]);

  const openPurchaseModal = () => {
    if (mpConnected === false) {
      Modal.confirm({
        title: 'Conecta o Mercado Pago primeiro',
        icon: <WarningOutlined style={{ color: '#faad14' }} />,
        content: (
          <div>
            <p>Crédito só serve pra enviar cobranças — e sem conta MP conectada você não recebe o dinheiro dos pagadores.</p>
            <p style={{ marginTop: 8, color: '#666' }}>Conecta a conta antes de comprar, evita ter que estornar depois.</p>
          </div>
        ),
        okText: 'Conectar agora',
        okButtonProps: { type: 'primary', style: { background: '#128C7E', borderColor: '#128C7E' } },
        cancelText: 'Depois',
        onOk: () => nav('/comunidade/financeiro/configuracoes'),
      });
      return;
    }
    setModal(true);
  };

  const resetFlow = () => {
    setStep(0);
    setSelectedPack(null);
    setMethod(null);
    setPixPurchase(null);
    setPixLoading(false);
    setCardSubmitting(false);
    setRegisterExpense(false);
  };

  // Enquanto o cartao tem uma cobranca em voo, fechar o modal (X, ESC, clique
  // fora) nao cancela o request — axios nao aborta so por causa do unmount —
  // entao o MP acaba cobrando e o usuario, achando que falhou, tenta pagar de
  // novo, gerando double-charge. Trava o fechamento so nessa janela; depois
  // que a resposta chega (sucesso ou falha) o modal volta a poder fechar.
  const closeBlocked = cardSubmitting;

  const closeModal = () => {
    if (closeBlocked) return;
    resetFlow();
    setModal(false);
  };

  const finishPaid = () => {
    load();
    onChange?.();
    setTimeout(closeModal, 2000);
  };

  const chooseMethod = async (m) => {
    setMethod(m);
    if (m === 'pix') {
      setPixLoading(true);
      try {
        const r = await api.post('/community/finance/credits/purchase/pix', {
          community_team_id: communityTeamId,
          pack_key: selectedPack.key,
          register_expense: registerExpense,
        });
        setPixPurchase(r.data);
        setStep(2);
      } catch (e) {
        const status = e.response?.status;
        if (status === 503) message.error('MP não configurado. Fala com o suporte.');
        else message.error(e.response?.data?.message || 'Falhou gerar o Pix');
      } finally {
        setPixLoading(false);
      }
    } else {
      setStep(2);
    }
  };

  return (
    <>
      <Card>
        <Statistic
          title="Créditos disponíveis"
          value={balance}
          prefix={<ThunderboltOutlined />}
          valueStyle={{ color: '#128C7E' }}
        />
        <Text type="secondary" style={{ fontSize: 11 }}>
          Cada crédito custa R$ 1. Gastos em:
          {' '}<Tooltip title="Ao criar uma cobrança pra 1 pessoa (email inicial)"><span style={{ textDecoration: 'underline dotted' }}>cobrança gerada</span></Tooltip> (1 créd)
          {' '}·{' '}
          <Tooltip title="Ao mandar reforço via WhatsApp pra quem não pagou"><span style={{ textDecoration: 'underline dotted' }}>WhatsApp de reforço</span></Tooltip> (1 créd)
        </Text>
        <br />
        <Button type="primary" icon={<CreditCardOutlined />}
          onClick={openPurchaseModal} style={{ marginTop: 12 }}>
          Comprar créditos
        </Button>
      </Card>

      <Modal
        open={modal}
        onCancel={closeModal}
        closable={!closeBlocked}
        maskClosable={!closeBlocked}
        keyboard={!closeBlocked}
        footer={null}
        title={
          step === 0 ? 'Escolha um pack de créditos'
            : step === 1 ? 'Como quer pagar?'
              : method === 'pix' ? 'Pagamento via Pix' : 'Pagamento com Cartão'
        }
        destroyOnClose
        width={720}
      >
        {step === 0 && (
          <Row gutter={[16, 16]} align="stretch">
            {packs.map(p => {
              const perCredit = p.credits > 0 ? p.price_brl / p.credits : 0;
              const withoutDiscount = p.credits * 1;
              const savings = withoutDiscount - p.price_brl;
              return (
                <Col xs={24} md={8} key={p.key} style={{ display: 'flex' }}>
                  <Card
                    size="small"
                    styles={{ body: { display: 'flex', flexDirection: 'column', height: '100%', padding: 16 } }}
                    style={{ width: '100%' }}
                  >
                    <div style={{ textAlign: 'center' }}>
                      <Title level={2} style={{ margin: 0, color: '#128C7E', lineHeight: 1 }}>{p.credits}</Title>
                      <Text style={{ fontSize: 13, color: '#666' }}>créditos</Text>
                    </div>

                    <div style={{ textAlign: 'center', marginTop: 16 }}>
                      <Text strong style={{ fontSize: 22 }}>{fmtBRL(p.price_brl)}</Text>
                    </div>

                    <div style={{ minHeight: 28, marginTop: 8, textAlign: 'center' }}>
                      {savings > 0 && (
                        <Tag color="green" style={{ margin: 0 }}>Economia de {fmtBRL(savings)}</Tag>
                      )}
                    </div>

                    <div style={{ textAlign: 'center', marginTop: 8 }}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        R$ {perCredit.toFixed(2).replace('.', ',')} por crédito
                      </Text>
                    </div>

                    <div style={{ flex: 1 }} />

                    <Button
                      type="primary"
                      block
                      onClick={() => { setSelectedPack(p); setStep(1); }}
                      style={{ marginTop: 16 }}
                    >
                      Comprar pack {p.key.toUpperCase()}
                    </Button>
                  </Card>
                </Col>
              );
            })}
            {packs.length === 0 && (
              <Col span={24}>
                <Text>Nenhum pack configurado. Fala com o suporte.</Text>
              </Col>
            )}
          </Row>
        )}

        {step === 1 && selectedPack && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Alert
              type="info"
              showIcon
              message={`Pack ${selectedPack.key.toUpperCase()}: ${selectedPack.credits} créditos por ${fmtBRL(selectedPack.price_brl)}`}
            />
            <div style={{ background: '#f5f5f5', padding: 12, borderRadius: 6 }}>
              <Checkbox
                checked={registerExpense}
                onChange={e => setRegisterExpense(e.target.checked)}
              >
                <Text strong>Lançar como saída na minha conta corrente</Text>
              </Checkbox>
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4, paddingLeft: 24 }}>
                {registerExpense
                  ? `Assim que o pagamento confirmar, vou registrar uma saída de ${fmtBRL(selectedPack.price_brl)} no extrato do time.`
                  : 'Marca essa opção se quer que essa compra apareça no extrato do time como despesa.'}
              </Text>
            </div>
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Button
                  type="primary"
                  block
                  size="large"
                  loading={pixLoading}
                  onClick={() => chooseMethod('pix')}
                >
                  Pagar com Pix
                </Button>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 4 }}>
                  Confirmação em segundos
                </Text>
              </Col>
              <Col xs={24} md={12}>
                <Button
                  type="default"
                  block
                  size="large"
                  disabled={!mpConfigured() || pixLoading}
                  onClick={() => chooseMethod('card')}
                >
                  Pagar com Cartão
                </Button>
                <Text type="secondary" style={{ fontSize: 11, display: 'block', textAlign: 'center', marginTop: 4 }}>
                  {mpConfigured() ? 'Débito imediato' : 'Cartão indisponível'}
                </Text>
              </Col>
            </Row>
            <Button onClick={resetFlow}>Voltar</Button>
          </Space>
        )}

        {step === 2 && method === 'pix' && pixPurchase && (
          <PixPaymentPanel
            purchase={pixPurchase}
            teamId={communityTeamId}
            onPaid={finishPaid}
            onExpired={() => setStep(1)}
          />
        )}

        {step === 2 && method === 'card' && selectedPack && (
          <CardPaymentPanel
            amount={selectedPack.price_brl}
            packKey={selectedPack.key}
            registerExpense={registerExpense}
            teamId={communityTeamId}
            onPaid={finishPaid}
            onCancel={() => setStep(1)}
            onSubmittingChange={setCardSubmitting}
          />
        )}
      </Modal>
    </>
  );
}
